import { useState, useEffect, useCallback, useMemo } from 'react'
import { numberFormat, reduce, setDocumentTitle } from 'shared/util'

import { SearchProvider } from 'features/search/context'

import { PrefectureResult } from 'interface/prefecture'

import { AreaBar } from 'components/areaBar'
import { Loading } from 'components/controls/loading'
import { ErrorInfo } from 'components/controls/errorInfo'
import { ColumnSplitter } from 'components/controls/splitter'

import {
  Box,
  Paper,
  Stack,
  StackProps,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  styled
} from '@mui/material'

import { MapIcon, DashBoardIcon } from 'shared/icons'

import { SisetuResult } from 'interface/sisetu'
import { SisetuPreviewDialog } from '../dialog/preview'

import { SisetuListBar } from '../components/list/bar'
import { SisetuList } from '../components/list'
import { SisetuMapView, useSisetuMap } from '../map'
import { SisetuMapLayerType } from '../map/types'

import { useSisetuAnalitycs } from './container'

import { useDashBoard } from 'features/dashBoard/hooks'
import { SisetuAnalyticsConfigure } from './configure'
import { DashBoardBar } from 'features/dashBoard/components/dashBoardBar'
import { ValueSpan } from 'components/controls/valueSpan'
import { DashBoard } from 'features/dashBoard'
import { SearchDetailResult, useGetSearchDetail } from 'interface/search'

import { useTenant } from 'features/tenant/context'
import { useTopBar } from 'components/topBar/container'
import { BreadCrumbItem } from 'components/topBar/types'
import { useSideMenu } from 'components/sideMenu/container'
import { SisetuSearchFlags } from 'features/sisetu/components/list/types'

const BoardName = 'sisetu'

const SisetuTabs = styled(Tabs)({
  '& .MuiTabs-list': {
    height: '100%'
  }
})
const SisetuTabProps = (tabName: string) => ({
  value: tabName,
  id: `sisetu-tab-${tabName}`,
  'aria-controls': `sisetu-tabpanel-${tabName}`,
  sx: {
    minHeight: 'auto',
  }
})

type SisatuAnalyticsPageProps = {
  searchId: number
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
} & StackProps
export const SisatuAnalyticsPage = ({
  searchId,
  ...others
}: SisatuAnalyticsPageProps) => {
  const searchStatus = useGetSearchDetail(searchId)
  if (searchStatus.loading) return <Loading/>
  if (searchStatus.error) return <ErrorInfo error={searchStatus.error}/>
  if (!searchStatus.data) return <></>
  return (
    <SearchProvider search={searchStatus.data}>
      <SisatuAnalyticsPageCore
        search={searchStatus.data}
        {...others}
      />
    </SearchProvider>
  )
}

type SisatuAnalyticsPageCoreProps = {
  search: SearchDetailResult
} & Omit<SisatuAnalyticsPageProps, "searchId">
export const SisatuAnalyticsPageCore = ({
	topBarState: { setBreadCrumbs },
	sideMenuState: { setActivePage },
  search,
  ...others
}: SisatuAnalyticsPageCoreProps) => {
  const tenant = useTenant()
  const [currentTab, setCurrentTab] = useState<'map' | 'analytics'>('map')
  const {
		loading, loaded, error,
		sisetuList,
    sortedList,
		block, setBlock,
		prefectureCodes, setPrefectureCodes,
		searchWord, setSearchWord,
		orderBy, setOrderBy,
		conditions, setConditions,
		profileId, setProfileId,
		selectedSisetuCode, setSelectedSisetuCode,
		openedSisetuCode, setOpenedSisetuCode
  } = useSisetuAnalitycs({
    search
  })
  const searchFlags = useMemo<SisetuSearchFlags>(
    () => {
      const { hasDpc, hasCancer } = reduce(search?.disease, (status, disease) => ({
        hasDpc: status.hasDpc || (disease.dpcCode !== null),
        hasCancer: status.hasCancer || (disease.cancerCode !== null)
      }), {
        hasDpc: false,
        hasCancer: false
      })
      const hasJournal = (search?.journalCount ?? 0) > 0 ? true : false
      const hasTrial = (search?.trialCount ?? 0) > 0 ? true : false
      const hasResearch = (search?.researchCount ?? 0) > 0 ? true : false
      const hasSenmoni = (search?.senmoni?.length ?? 0) > 0 ? true : false
      return {
        hasDpc, hasCancer, hasJournal, hasTrial, hasResearch, hasSenmoni
      }
    },
    [search]
  )
  const dashBoard = useDashBoard<SisetuResult>({
    profileKey: BoardName,
    configure: SisetuAnalyticsConfigure,
    conditions, setConditions,
    profileId, setProfileId
  })

  const {
    mapRef,
    invalidMapFlg,
    showMapLayer, setShowMapLayer,
    mapZooming, setMapZooming,
    panTo,
    fitMap,
  } = useSisetuMap()

  const openSisetu = useCallback(
    (sisetuCode: string) => {
      setOpenedSisetuCode(sisetuCode)
    },
    [setOpenedSisetuCode]
  )
  const closeModal = useCallback(
    () => {
      setOpenedSisetuCode(null)
    },
    [setOpenedSisetuCode]
  )
  const selectedSisetu = useMemo(
    () => {
      if (!selectedSisetuCode) return null
      return sortedList.find(s => s.sisetuCode === selectedSisetuCode) ?? null
    },
    [selectedSisetuCode, sortedList]
  )
  useEffect(
    () => {
      if (selectedSisetu) panTo(selectedSisetu, true)
    },
    [selectedSisetu, panTo]
  )
  const selectPrefecture = useCallback(
    (prefectures: PrefectureResult[]) => {
      const codes = prefectures.map(prefecture => prefecture.prefectureCode)
      setPrefectureCodes(codes)
      setSelectedSisetuCode(null)
      fitMap(prefectures)
    },
    [setPrefectureCodes, setSelectedSisetuCode, fitMap]
  )
  const selectSisetuCode = useCallback(
    (sisetuCode: string | null) => {
      setSelectedSisetuCode(sisetuCode)
      if (sisetuCode) {
        const sisetu = sortedList.find(s => s.sisetuCode === sisetuCode)
        if (sisetu) {
          panTo(sisetu, true)
        }
      }
    },
    [setSelectedSisetuCode, panTo, sortedList]
  )
  useEffect(
    () => {
      setDocumentTitle('施設分析', search.title, tenant.tenantName)
      const crumbs: BreadCrumbItem[] = []
      if (search.manufacturer?.manufacturerName) {
        crumbs.push({
          label: search.manufacturer.manufacturerName,
          url: `/${tenant.domain}/search/list#manufacturer-${search.manufacturer.manufacturerId}`
        })
      }
      crumbs.push({
        label: search.title,
        url: `/${tenant.domain}/search/${search.searchId}/home`
      })
      crumbs.push({ label: '施設分析' })
      setBreadCrumbs(crumbs)
      setActivePage('sisetu')
    },
    [tenant, search, setBreadCrumbs, setActivePage]
  )
  return (
    <>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        sx={{
          position: 'relative',
          overflow: 'hidden',
        }}
        {...others}
      >
        <AreaBar
          defaultBlockCode={block}
          onSelectedBlock={setBlock}
          defaultPrefectureCodes={prefectureCodes}
          onSelectedPrefecture={selectPrefecture}
        />

        <ColumnSplitter
          onDragEnd={() => {
            mapRef.current?.invalidateSize()
            invalidMapFlg.current = true
          }}
        >
          <Paper square sx={{ position: 'relative' }}>
            {loading && <Loading percent={loaded}/>}

            <Stack direction="column" overflow="hidden" height="100%">
              <SisetuListBar
                searchFlags={searchFlags}
                searchWord={searchWord}
                onChangeSearchWord={setSearchWord}
                orderBy={orderBy}
                onChangeOrderBy={setOrderBy}
              />

              {error && <ErrorInfo error={error} padding={1} />}

              <SisetuList
                flexGrow={1}
                loading={loading}
                sisetuList={sortedList}
                selectedSisetuCode={selectedSisetuCode}
                onSelectSisetu={(sisetuCode) => {
                  selectSisetuCode(sisetuCode)
                  if (currentTab === 'analytics') {
                    openSisetu(sisetuCode)
                  }
                }}
              />
            </Stack>
          </Paper>

          <Paper square sx={{ position: 'relative' }}>
            <Stack direction="column" sx={{ width: '100%', height: '100%' }}>
              <Stack
                direction="row"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <SisetuTabs
                  value={currentTab}
                  onChange={(_, value) => setCurrentTab(value)}
                  sx={{ flexGrow: 1, height: '100%' }}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="sisetu-tabs"
                >
                  <Tab
                    key="map"
                    icon={<MapIcon />}
                    iconPosition="start"
                    label='地図'
                    {...SisetuTabProps('map')}
                  />
                  <Tab
                    key="analytics"
                    icon={<DashBoardIcon />}
                    iconPosition="start"
                    label='分析'
                    {...SisetuTabProps('analytics')}
                  />
                </SisetuTabs>

                {currentTab === 'map' ? (
                  <Stack direction="row" padding={1}>
                    <ToggleButtonGroup
                      color="primary"
                      value={showMapLayer}
                      exclusive
                      onChange={(event, value: string) => {
                        if (value && value.length) {
                          setShowMapLayer(value as SisetuMapLayerType)
                        }
                      }}
                      aria-label="map-layer"
                      size="small"
                      sx={{ flexGrow: 1 }}
                    >
                      <ToggleButton value="pin" sx={{ whiteSpace: 'nowrap' }}>
                        場所
                      </ToggleButton>
                      <ToggleButton value="dpc" sx={{ whiteSpace: 'nowrap' }}>
                        DPC
                      </ToggleButton>
                      <ToggleButton value="cancer" sx={{ whiteSpace: 'nowrap' }}>
                        がん
                      </ToggleButton>
                      <ToggleButton value="trial" sx={{ whiteSpace: 'nowrap' }}>
                        治験
                      </ToggleButton>
                      <ToggleButton value="journal" sx={{ whiteSpace: 'nowrap' }}>
                        論文
                      </ToggleButton>
                      <ToggleButton value="senmoni" sx={{ whiteSpace: 'nowrap' }}>
                        専門医
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                ) : currentTab === 'analytics' ? (
                  <DashBoardBar
                    search={search}
                    dashBoard={dashBoard}
                    leftItems={
                      <Stack direction="row" paddingX={1} spacing={1}>
                        {sisetuList.length === sortedList.length ? (
                          <ValueSpan text={numberFormat(sisetuList.length, '-')} suffix='施設'/>
                        ) : (
                          <>
                            <ValueSpan text={numberFormat(sortedList.length, '-')} suffix='/'/>
                            <ValueSpan text={numberFormat(sisetuList.length, '-')} suffix='施設'/>
                          </>
                        )}
                      </Stack>
                    }
                  />
                ) : (
                  <></>
                )}
              </Stack>

              <Box
                role="tabpanel"
                hidden={currentTab !== 'map'}
                id="sisetu-tabpanel-map"
                aria-labelledby="sisetu-tab-map"
                sx={{
                  width: '100%',
                  flexGrow: 1,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <SisetuMapView
                  ref={mapRef}
                  key="sisetuMapView"
                  sisetuList={sortedList}
                  selectedSisetuCode={selectedSisetuCode}
                  zooming={mapZooming}
                  onZoomEnd={() => {
                    setMapZooming(false)
                  }}
                  showLayer={showMapLayer}
                  onSisetuOpen={openSisetu}
                />
              </Box>

              <Box
                role="tabpanel"
                hidden={currentTab !== 'analytics'}
                id="sisetu-tabpanel-analytics"
                aria-labelledby="sisetu-tab-analytics"
                sx={{
                  width: '100%',
                  flexGrow: 1,
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                <DashBoard
                  dashBoard={dashBoard}
                  search={search ?? undefined}
                  dataList={sisetuList}
                  onSelected={openSisetu}
                  flexGrow={1}
                />
              </Box>
            </Stack>
          </Paper>
        </ColumnSplitter>
      </Stack>

      {openedSisetuCode ? (
        <SisetuPreviewDialog
          sisetuList={sortedList}
          sisetuCode={openedSisetuCode}
          onClose={closeModal}
        />
      ) : <></>}
    </>
  )
}
